'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Web application penetration testing',
    description:
      'Manual, in-depth testing of web applications against OWASP Top 10 and beyond. Business-logic flaws, authentication bypasses, authorisation issues, and injection vulnerabilities that automated scanners consistently miss.',
  },
  {
    tag: '[02]',
    title: 'API and microservices testing',
    description:
      'REST, GraphQL, and gRPC APIs tested for broken object-level authorisation, mass assignment, rate limiting bypass, and data exposure. Coverage across every endpoint, not just the documented ones.',
  },
  {
    tag: '[03]',
    title: 'Infrastructure and network penetration testing',
    description:
      'Internal and external network assessments covering exposed services, misconfigurations, lateral movement paths, and privilege escalation vectors. Cloud infrastructure included as standard.',
  },
  {
    tag: '[04]',
    title: 'Mobile application testing',
    description:
      'iOS and Android application testing covering insecure data storage, improper session handling, certificate pinning bypass, and reverse engineering. Aligned to OWASP MASVS.',
  },
];

const outcomes = [
  { metric: 'Manual', label: 'Senior-led testing, not automated scanner output' },
  { metric: 'OWASP', label: 'Aligned to OWASP Top 10, ASVS, and MASVS' },
  { metric: '48hr', label: 'Critical finding notification during engagement' },
  { metric: 'Remediation', label: 'Included retesting of all critical and high findings' },
];

const methodology = [
  {
    phase: '01',
    title: 'Scoping & threat modelling',
    desc: 'Define scope, objectives, and rules of engagement. Threat model the target to focus testing effort on the highest-risk areas.',
  },
  {
    phase: '02',
    title: 'Reconnaissance & enumeration',
    desc: 'Passive and active reconnaissance. Attack surface mapping, technology fingerprinting, and entry point identification.',
  },
  {
    phase: '03',
    title: 'Exploitation & post-exploitation',
    desc: 'Manual exploitation of identified vulnerabilities. Chaining findings to demonstrate real-world impact and business risk.',
  },
  {
    phase: '04',
    title: 'Reporting & remediation support',
    desc: 'Clear, actionable report with executive summary, technical findings, CVSS scores, and prioritised remediation guidance. Retesting included.',
  },
];

const techStack = [
  { category: 'Web & API', tools: ['Burp Suite Pro', 'OWASP ZAP', 'Postman', 'ffuf', 'nuclei'] },
  { category: 'Network', tools: ['Nmap', 'Metasploit', 'Nessus', 'BloodHound', 'Impacket'] },
  { category: 'Mobile', tools: ['Frida', 'Objection', 'MobSF', 'apktool', 'jadx'] },
  { category: 'Cloud', tools: ['ScoutSuite', 'Pacu', 'CloudSploit', 'Prowler'] },
  { category: 'Frameworks', tools: ['OWASP Top 10', 'OWASP ASVS', 'OWASP MASVS', 'PTES', 'OSSTMM'] },
  { category: 'Reporting', tools: ['CVSS 4.0', 'CWE', 'MITRE ATT&CK', 'Custom risk scoring'] },
];

const differentiators = [
  { title: 'Senior-only testers', desc: 'Every engagement is led by a senior penetration tester. No juniors, no automated-only outputs.' },
  { title: 'Business-logic focus', desc: 'We test the logic of your application, not just the OWASP checklist. The findings that matter most are the ones automated tools miss.' },
  { title: 'Remediation included', desc: 'Retesting of all critical and high findings is included as standard. We do not close an engagement until the critical issues are resolved.' },
  { title: 'Compliance-ready reports', desc: 'Reports formatted to satisfy SOC 2, ISO 27001, PCI-DSS, and DORA audit requirements.' },
];

export default function PentestingPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-100px', right: '10%', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '450px', height: '450px', bottom: '-100px', left: '-50px', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Penetration Testing</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Penetration Testing]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Manual testing that finds<br />
              <span className="text-[#80edfa]">what scanners miss.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Senior-led penetration testing across web applications, APIs, infrastructure, and mobile. Business-logic vulnerabilities, chained exploits, and real-world attack scenarios, not automated scanner output dressed up as a pentest.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-3xl lg:text-4xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-sm text-[#555] leading-snug">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Coverage</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What we test</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Methodology</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">How we work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {methodology?.map((p, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[{p?.phase}]</p>
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{p?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{p?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Tooling & frameworks</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What we use</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {techStack?.map((t, i) => (
              <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.6rem] text-[#555] uppercase tracking-widest mb-4">{t?.category}</p>
                <div className="flex flex-wrap gap-2">
                  {t?.tools?.map((tool) => (
                    <span key={tool} className="font-mono-brand text-[0.65rem] text-[#aaa] border border-[#222] px-3 py-1.5 uppercase tracking-wide">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Why TechSonar</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What makes this different</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators?.map((d, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-4" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-2">{d?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{d?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Pentest callout */}
      <section className="border-t border-[#222] py-16">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Link
            href="/services/ai-pentesting"
            className="group block bg-[#0a0f14] border border-[#80edfa]/20 hover:border-[#80edfa]/50 p-8 lg:p-12 transition-all duration-300 fade-in-up"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono-brand text-[0.6rem] text-[#80edfa] border border-[#80edfa]/40 px-2 py-0.5 uppercase tracking-widest">Specialisation</span>
                </div>
                <h2 className="font-heading text-2xl lg:text-3xl text-[#f5f5f8] font-medium mb-3 group-hover:text-[#80edfa] transition-colors duration-300">AI Penetration Testing</h2>
                <p className="text-sm text-[#666] leading-relaxed max-w-[680px] group-hover:text-[#aaa] transition-colors duration-300">
                  We specialise in security testing of AI and ML systems, LLM prompt injection, model extraction, training data poisoning, and adversarial attacks. Purpose-built for organisations building or deploying AI.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Explore AI pentesting</span>
                <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
                  <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium mb-6">
              Ready to find out what is really exposed?
            </h2>
            <p className="text-[#666] text-lg mb-8 leading-relaxed">
              Talk to us about your application, infrastructure, or API. We will scope an engagement that gives you real answers, not a report full of scanner output.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services" className="btn-secondary">View all services</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
